import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// ISO 4217 fiat codes that Intl.NumberFormat accepts for currency formatting
const FIAT_CURRENCY_CODES = new Set(["USD", "EUR", "GBP", "CAD", "AUD", "JPY", "CHF", "MXN", "BRL", "COP"]);

export const formatCurrency = (
  amount: string | number,
  options?: {
    symbol?: string;
    decimals?: number;
    minimumFractionDigits?: number;
  }
) => {
  const { symbol, decimals = 6, minimumFractionDigits = 2 } = options || {};
  const isFiat = symbol && FIAT_CURRENCY_CODES.has(symbol.toUpperCase());
  const minFractionDigits = Math.min(minimumFractionDigits, decimals);
  const formatter = new Intl.NumberFormat("en-US", {
    style: isFiat ? "currency" : "decimal",
    ...(isFiat && { currency: symbol.toUpperCase() }),
    minimumFractionDigits: minFractionDigits,
    maximumFractionDigits: decimals
  });
  return formatter.format(Number(amount));
};

export const FIAT_AMOUNT_DECIMALS = 2;
export const CRYPTO_AMOUNT_DECIMALS = 3;

/**
 * Formats an amount with the precision fixed by its asset type, so the same
 * value reads identically at every step of a transaction flow.
 */
export const formatAssetAmount = (amount: string | number, currencyType: string) => {
  const decimals = currencyType === "FIAT" ? FIAT_AMOUNT_DECIMALS : CRYPTO_AMOUNT_DECIMALS;
  return formatCurrency(amount, { decimals, minimumFractionDigits: decimals });
};

export function formatDateTime(date: Date, locale: string = "en-US"): string {
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "medium",
    timeStyle: "short"
  }).format(date);
}

export function truncateAddress(address: string): string {
  return `${address.slice(0, 8)}...${address.slice(-6)}`;
}

export function formatDate(
  date: Date | string | number | undefined,
  opts: Intl.DateTimeFormatOptions = {},
  locale: string = "en-US"
) {
  if (!date) return "";

  try {
    return new Intl.DateTimeFormat(locale, {
      month: opts.month ?? "long",
      day: opts.day ?? "numeric",
      year: opts.year ?? "numeric",
      ...opts
    }).format(new Date(date));
  } catch (_err) {
    console.error("Error formatting date", _err);
    return "";
  }
}

export function formatCryptoAmount(
  amount: number | string,
  maxDecimals: number = 6
): string {
  const numAmount = typeof amount === "string" ? parseFloat(amount) : amount;

  if (isNaN(numAmount)) return "0";

  const formatted = numAmount.toFixed(maxDecimals);
  const trimmed = formatted.replace(/\.?0+$/, "");

  const parts = trimmed.split(".");
  const integerPart = new Intl.NumberFormat("en-US").format(
    parseInt(parts[0], 10)
  );

  return parts.length > 1 ? `${integerPart}.${parts[1]}` : integerPart;
}
